import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Child {
  id: string;
  full_name: string;
}

interface ExistingResult {
  child_id: string;
  score: number | null;
  notes: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  examId: string | null;
  examTitle: string;
  classId: string | null;
  nurseryId: string;
  onSuccess: () => void;
}

export default function ExamResultsDialog({ open, onOpenChange, examId, examTitle, classId, nurseryId, onSuccess }: Props) {
  const { toast } = useToast();
  const [children, setChildren] = useState<Child[]>([]);
  const [results, setResults] = useState<Record<string, { score: string; notes: string }>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !examId) return;
    const load = async () => {
      setLoading(true);
      // Fetch children (filtered by class if available)
      let q = supabase.from("children").select("id, full_name").eq("nursery_id", nurseryId);
      if (classId) q = q.eq("class_id", classId);
      const { data: kids } = await q.order("full_name");
      setChildren(kids || []);

      // Fetch existing results
      const { data: existing } = await supabase.from("exam_results").select("child_id, score, notes").eq("exam_id", examId);
      const map: Record<string, { score: string; notes: string }> = {};
      (kids || []).forEach((kid) => {
        const ex = (existing || []).find((r) => r.child_id === kid.id);
        map[kid.id] = { score: ex?.score?.toString() || "", notes: ex?.notes || "" };
      });
      setResults(map);
      setLoading(false);
    };
    load();
  }, [open, examId, classId, nurseryId]);

  const handleSave = async () => {
    if (!examId) return;
    setSaving(true);
    const upserts = Object.entries(results)
      .filter(([_, v]) => v.score !== "")
      .map(([childId, v]) => ({
        exam_id: examId,
        child_id: childId,
        score: parseFloat(v.score),
        notes: v.notes || null,
      }));

    if (upserts.length === 0) {
      toast({ title: "لا توجد نتائج لحفظها" });
      setSaving(false);
      return;
    }

    // Delete existing then insert
    await supabase.from("exam_results").delete().eq("exam_id", examId);
    const { error } = await supabase.from("exam_results").insert(upserts);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم حفظ النتائج بنجاح" });
      onOpenChange(false);
      onSuccess();
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>نتائج: {examTitle}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : children.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">لا يوجد أطفال مسجلين</p>
        ) : (
          <div className="space-y-4">
            {children.map((child) => (
              <div key={child.id} className="flex items-start gap-3 p-3 rounded-lg border border-border">
                <div className="flex-1 space-y-2">
                  <Label className="font-medium">{child.full_name}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="الدرجة"
                      className="w-24"
                      value={results[child.id]?.score || ""}
                      onChange={(e) => setResults((prev) => ({ ...prev, [child.id]: { ...prev[child.id], score: e.target.value } }))}
                      min={0}
                      max={100}
                    />
                    <Input
                      placeholder="ملاحظات"
                      className="flex-1"
                      value={results[child.id]?.notes || ""}
                      onChange={(e) => setResults((prev) => ({ ...prev, [child.id]: { ...prev[child.id], notes: e.target.value } }))}
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button onClick={handleSave} className="w-full" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ النتائج"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
